<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const profileMenuOpen = ref(false)

function toggleProfileMenu(): void {
  profileMenuOpen.value = !profileMenuOpen.value
}

function closeProfileMenu(): void {
  profileMenuOpen.value = false
}

function delayedCloseProfileMenu(): void {
  window.setTimeout(closeProfileMenu, 150)
}

function handleLogout(): void {
  closeProfileMenu()
  auth.logout()
}
</script>

<template>
  <div id="warren-app" class="min-h-screen bg-dark-navy font-sans">
    <!-- Top navigation bar -->
    <nav class="border-b border-primary-blue/30 bg-dark-navy/95 backdrop-blur-sm">
      <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
        <router-link to="/" class="flex items-center gap-3">
          <img src="/oi_logo.avif" alt="ORBITInsure" class="h-12 w-12 rounded-lg object-contain" />
          <div>
            <span class="text-lg font-semibold text-white">Warren AI</span>
            <span class="ml-2 text-xs text-accent-teal">by ORBITInsure</span>
          </div>
        </router-link>

        <div class="flex items-center gap-4">
          <template v-if="auth.isAuthenticated">
            <!-- Customer nav -->
            <router-link
              v-if="auth.user?.role === 'customer'"
              to="/dashboard"
              class="text-sm text-gray-300 transition hover:text-accent-teal"
            >
              Dashboard
            </router-link>
            <!-- Underwriter nav -->
            <router-link
              v-if="auth.user?.role === 'underwriter'"
              to="/underwriter"
              class="text-sm text-gray-300 transition hover:text-accent-teal"
            >
              Underwriting
            </router-link>
            <!-- Admin nav -->
            <router-link
              v-if="auth.user?.role === 'admin'"
              to="/admin"
              class="text-sm text-gray-300 transition hover:text-accent-teal"
            >
              Admin
            </router-link>

            <!-- Profile dropdown -->
            <div class="relative">
              <button
                class="flex items-center gap-2 rounded-lg bg-primary-blue/30 px-3 py-1.5 text-sm text-gray-200 transition hover:bg-primary-blue/50"
                @click="toggleProfileMenu"
                @blur="delayedCloseProfileMenu"
              >
                <div class="flex h-7 w-7 items-center justify-center rounded-full bg-accent-teal text-xs font-semibold text-dark-navy">
                  {{ auth.user?.firstName?.charAt(0) ?? '' }}{{ auth.user?.lastName?.charAt(0) ?? '' }}
                </div>
                <span>{{ auth.displayName }}</span>
                <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <div
                v-show="profileMenuOpen"
                class="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-primary-blue/30 bg-dark-navy shadow-xl"
              >
                <div class="border-b border-primary-blue/20 px-4 py-3">
                  <p class="text-sm font-medium text-white">{{ auth.displayName }}</p>
                  <p class="text-xs text-gray-400">{{ auth.user?.email }}</p>
                  <span class="mt-1 inline-block rounded-full bg-accent-teal/20 px-2 py-0.5 text-xs capitalize text-accent-teal">
                    {{ auth.user?.role }}
                  </span>
                </div>
                <div class="py-1">
                  <button
                    class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-300 transition hover:bg-primary-blue/20 hover:text-white"
                    @click="closeProfileMenu"
                  >
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </button>
                  <button
                    class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-300 transition hover:bg-primary-blue/20 hover:text-white"
                    @click="handleLogout"
                  >
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <button
              class="rounded-md bg-accent-teal px-4 py-1.5 text-sm font-medium text-dark-navy transition hover:bg-accent-teal/90"
              @click="auth.login"
            >
              Sign In
            </button>
          </template>
        </div>
      </div>
    </nav>

    <!-- Main content area -->
    <main>
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="border-t border-primary-blue/20 py-6 text-center text-xs text-gray-500">
      <p>Warren AI &mdash; GNSS Parametric Insurance Platform</p>
      <p class="mt-1">Lloyd's Syndicate Demonstration &mdash; ORBITInsure &copy; 2026</p>
    </footer>
  </div>
</template>

<style scoped>
#warren-app {
  display: flex;
  flex-direction: column;
}

main {
  flex: 1;
}
</style>
